import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, demoEnabled } from '../lib/auth';
import { SplashAuthShell } from './SplashAuthShell';
import { TRACKED_REG } from '../lib/flightTrack';

const DEMO_SEATS = [
  { role: 'admin' as const,              to: '/admin', label: 'Owner',                   sub: 'Run the airplane. Approvals, revenue, maintenance, the whole operation.', primary: true },
  { role: 'lessee_active' as const,      to: '/',      label: 'Active pilot',            sub: 'Cleared to fly. Book, preflight, log a flight, see the bill.' },
  { role: 'lessee_incomplete' as const,  to: '/',      label: 'Brand-new pilot',         sub: 'Just signed up. Watch the onboarding and the document reader.' },
  { role: 'lessee_pending' as const,     to: '/',      label: 'Waiting on approval',     sub: 'Everything uploaded, owner has not activated them yet.' },
];

/** The demo build's front door. It sits ABOVE the sign-in form on purpose:
 *  nobody arriving from the marketing site has a password, and a login form is
 *  the fastest way to lose them. */
function DemoSeats({
  onPick,
}: {
  onPick: (role: (typeof DEMO_SEATS)[number]['role'], to: string) => void;
}) {
  return (
    <div className="demo-seats relative mb-7 overflow-hidden rounded-2xl border-2 border-sky-500/70 bg-white p-5 shadow-[0_18px_50px_-24px_rgba(14,110,170,.55)]">
      <span className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-400/15 blur-2xl" />
      <div className="relative">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="inline-flex items-center rounded-full bg-sky-600 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.16em] text-white">
            Start here
          </span>
          <span className="text-xs font-medium uppercase tracking-widest text-ink-muted">No password needed</span>
        </div>
        <h2 className="mt-3 text-xl font-bold leading-snug text-navy-900">
          Pick a seat and the whole app opens up.
        </h2>
        <p className="mt-1.5 text-sm text-ink-muted">
          Sample data. Nothing is saved and nothing is sent. Most people want the owner view.
        </p>

        <div className="mt-4 grid gap-2.5">
          {DEMO_SEATS.map((s) => (
            <button
              key={s.role}
              type="button"
              onClick={() => onPick(s.role, s.to)}
              className={`group flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all ${
                s.primary
                  ? 'bg-sky-600 text-white shadow-md hover:bg-sky-700 hover:shadow-lg'
                  : 'border border-slate-200 bg-white text-navy-900 hover:border-sky-400 hover:bg-sky-50/60'
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">{s.label}</span>
                <span className={`mt-0.5 block text-xs leading-snug ${s.primary ? 'text-white/80' : 'text-ink-muted'}`}>
                  {s.sub}
                </span>
              </span>
              <svg
                width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                className="shrink-0 translate-x-0 transition-transform group-hover:translate-x-1"
                stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { signIn, signInWithGoogle, resetPassword, configured, enterDemo } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setInfo(null);
    setBusy(true);
    try {
      await signIn(email, password);
      nav('/', { replace: true });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Sign in failed.';
      setErr(msg.replace('Firebase: ', ''));
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setErr(null);
    setInfo(null);
    setBusy(true);
    try {
      await signInWithGoogle();
      nav('/', { replace: true });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Google sign-in failed.';
      if (!/popup-closed|cancelled-popup|popup_closed/i.test(msg)) {
        setErr(msg.replace('Firebase: ', ''));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setErr(null);
    setInfo(null);
    if (!email) {
      setErr('Enter your email above first.');
      return;
    }
    try {
      await resetPassword(email);
      setInfo('Password reset link sent. Check your email.');
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not send reset email.');
    }
  }

  return (
    <SplashAuthShell
      title={demoEnabled() ? 'Try the app' : 'Sign in'}
      subtitle={
        demoEnabled()
          ? `This is a live demo of the software that runs ${TRACKED_REG}.`
          : 'Access your AJM Aviation lessee account.'
      }
    >
      {demoEnabled() && (
        <>
          <DemoSeats
            onPick={(role, to) => {
              enterDemo(role);
              nav(to, { replace: true });
            }}
          />
        </>
      )}
      {/* On the demo build there is no account to sign into, and a login form is the
          fastest way to lose somebody who arrived from the marketing site. */}
      {!demoEnabled() && (
        <>
      {!configured && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="font-semibold">Setup pending.</strong> Firebase credentials haven’t
          been added yet. See <code>SETUP.md</code>.
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {err && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {err}
          </div>
        )}
        {info && (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            {info}
          </div>
        )}
        <button type="submit" disabled={busy || !configured} className="btn-primary w-full">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-ink-muted">
        <span className="h-px flex-1 bg-slate-200" />
        or
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <button
        type="button"
        onClick={onGoogle}
        disabled={busy || !configured}
        className="btn-secondary w-full inline-flex items-center justify-center gap-2"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
          <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
          <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
          <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
        </svg>
        Continue with Google
      </button>

      <div className="mt-5 text-center text-sm">
        <button type="button" onClick={onReset} className="text-ink-muted hover:text-navy-900 underline">
          Forgot password?
        </button>
      </div>

      <div className="mt-6 rounded-2xl bg-slate-50 px-4 py-4 text-center ring-1 ring-slate-900/[0.06]">
        <p className="text-sm text-ink-muted">New pilot with AJM Aviation?</p>
        <Link
          to="/register"
          className="mt-2 inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-navy-800 shadow-sm transition-colors hover:bg-slate-50"
        >
          Create your pilot account
        </Link>
      </div>
        </>
      )}
    </SplashAuthShell>
  );
}
